import { useState, useEffect } from 'react';
import { Copy, Check, X, AlertTriangle } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { extractErrorMessage } from '@/lib/api';

interface ErrorDetailsModalProps {
  error: unknown;
  isOpen: boolean;
  onClose: () => void;
}

function getErrorStack(error: unknown): string | null {
  if (error instanceof Error && error.stack) return error.stack;
  return null;
}

/**
 * Raw error payload behind the friendly error screens, so a user can copy
 * something useful into a bug report. The stack trace is dev-only -- in
 * production it would leak internals without helping the person reading it.
 */
export function ErrorDetailsModal({ error, isOpen, onClose }: ErrorDetailsModalProps) {
  const { t } = useTranslation('errors');
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (!isOpen) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  const errorMessage = extractErrorMessage(error, t('generic'));
  const errorStack = getErrorStack(error);
  const isDev = import.meta.env.DEV;
  const fullErrorText = errorStack
    ? `Error: ${errorMessage}\n\nStack Trace:\n${errorStack}`
    : `Error: ${errorMessage}`;

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(fullErrorText);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // Clipboard API unavailable (older browser or insecure context).
      setCopied(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div
        role="dialog"
        aria-modal="true"
        aria-label={t('details.title')}
        className="flex max-h-[80vh] w-full max-w-2xl flex-col rounded-lg bg-white shadow-xl"
      >
        <div className="flex items-center justify-between border-b border-gray-200 px-6 py-4">
          <div className="flex items-center gap-3">
            <div className="flex h-8 w-8 items-center justify-center rounded-full bg-red-100">
              <AlertTriangle className="h-4 w-4 text-red-600" aria-hidden="true" />
            </div>
            <h2 className="text-lg font-semibold">{t('details.title')}</h2>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label={t('details.close')}
            className="rounded-md p-1 transition-colors hover:bg-gray-100"
          >
            <X className="h-5 w-5 text-gray-500" aria-hidden="true" />
          </button>
        </div>

        <div className="flex-1 space-y-4 overflow-auto px-6 py-4">
          <div>
            <h3 className="mb-1 text-sm font-medium text-gray-500">{t('details.message')}</h3>
            <p className="font-medium text-red-600">{errorMessage}</p>
          </div>

          {isDev && errorStack ? (
            <div>
              <h3 className="mb-1 text-sm font-medium text-gray-500">{t('details.stackTrace')}</h3>
              <pre className="max-h-64 overflow-auto rounded-lg bg-gray-100 p-4 font-mono text-xs text-gray-700">
                {errorStack}
              </pre>
            </div>
          ) : (
            <p className="text-sm text-gray-500">{t('details.stackDevOnly')}</p>
          )}
        </div>

        <div className="flex items-center justify-between border-t border-gray-200 px-6 py-4">
          <button
            type="button"
            onClick={handleCopy}
            className="inline-flex items-center gap-2 text-sm text-gray-600 transition-colors hover:text-gray-900"
          >
            {copied ? (
              <>
                <Check className="h-4 w-4 text-green-500" aria-hidden="true" />
                {t('details.copied')}
              </>
            ) : (
              <>
                <Copy className="h-4 w-4" aria-hidden="true" />
                {t('details.copyToClipboard')}
              </>
            )}
          </button>
          <button type="button" onClick={onClose} className="btn-secondary">
            {t('details.close')}
          </button>
        </div>
      </div>
    </div>
  );
}
